import { ReactNode, useEffect, useMemo, useRef } from 'react'
import { Box, Message, Modal, Pluralize, Text, BaseLink } from 'ui'
import {
  DEFAULT_CANCELLATION_POLICY_WINDOW,
  useCancellationPolicy,
} from 'pages/HomePage/ShiftCard/useCancellationPolicy'
import { useWorkerStats } from 'pages/HomePage/ShiftCard/useWorkerStats'
import { getLocalTimezone } from 'lib/time'
import { formatInTimeZone } from 'date-fns-tz'
import { useShiftEditor } from './useShiftEditor'
import { ShiftEditData, WorkStatusEnum } from 'api/shift'
import { track } from 'lib/amplitude'
import {
  formatPolicyWindowListCharge,
  formatPolicyWindowRemoveWorkerCharge,
  getPolicyWindowKey,
  getPolicyWindowTrackingProperties,
} from 'lib/cancellationPolicy'

interface Props {
  trigger: ReactNode
  shift: ShiftEditData
}

const policyUrl =
  'https://storage.googleapis.com/workwhile-terms-docs/static/ww-customer-agreement/ww_customer_agreement_20201204.pdf'

export function ReduceWorkersModal(props: Props) {
  const { shiftData, submitShiftData } = useShiftEditor()
  const { trigger, shift } = props
  const closeModalRef = useRef<() => void>(null)

  const { policyWindow, isStandard } = useCancellationPolicy(shift)
  const { workers, workersNeeded, extraWorkers } = useWorkerStats(shift)
  const timezone = shift?.location?.address?.timezone ?? getLocalTimezone()
  const cancellationPolicyWindows =
    shift.cancellationPolicy?.policyWindows ||
    DEFAULT_CANCELLATION_POLICY_WINDOW

  const scheduledWorkers = useMemo(() => {
    return workers.filter((w) => w.status === WorkStatusEnum.Scheduled).length
  }, [workers])

  const numWorkersToRemove = useMemo(
    () =>
      shiftData?.staff?.numWorkers
        ? Math.max(0, scheduledWorkers - shiftData?.staff?.numWorkers)
        : 0,
    [shiftData?.staff?.numWorkers, scheduledWorkers]
  )

  useEffect(() => {
    track('impression, reduce_workers_modal_content', {
      numWorkersToRemove,
      ...getPolicyWindowTrackingProperties(policyWindow),
    })
  }, [numWorkersToRemove, policyWindow])

  const handleSubmit = () => {
    submitShiftData()
  }

  return (
    <Modal
      trigger={trigger}
      title={
        'Are you sure you want to reduce the number of workers on this shift?'
      }
      okText={'Confirm'}
      onOk={({ closeModal }) => {
        closeModalRef.current = closeModal
        handleSubmit()
      }}
    >
      <Box mb={3}>
        {shift.position?.name} shift starting at{' '}
        {formatInTimeZone(shift.startsAt, timezone, 'EEEE MMM dd, hh:mma zzz')}
      </Box>
      <Box mb={3}>
        <Box mb={3}>
          <Text fontWeight={'bold'}>Original</Text> Workers Requested:{' '}
          {workersNeeded}
        </Box>
        <Box>
          <Text fontWeight={'bold'}>New</Text> Workers Requested:{' '}
          {shiftData?.staff?.numWorkers}
        </Box>
      </Box>
      <Box mb={3}>
        <Pluralize
          count={scheduledWorkers}
          oneText={'worker'}
          manyText={'workers'}
        />{' '}
        {extraWorkers > 0 && (
          <>
            and{' '}
            <Pluralize
              count={extraWorkers}
              oneText={'backup'}
              manyText={'backups'}
            />
          </>
        )}{' '}
        are currently scheduled for this shift.{' '}
        {numWorkersToRemove > 0 || extraWorkers > 0 ? (
          <>
            {numWorkersToRemove > 0 && (
              <Pluralize
                count={numWorkersToRemove}
                oneText={'worker'}
                manyText={'workers'}
              />
            )}
            {numWorkersToRemove > 0 && extraWorkers > 0 && ' along with '}
            {extraWorkers > 0 && 'some backups'}
            {' will be removed and notified of this cancellation.'}
          </>
        ) : null}
      </Box>
      {numWorkersToRemove > 0 && policyWindow ? (
        <>
          <Message variant={'warning'} mb={3}>
            Shift is within {policyWindow.cutoffTimeHours} hours cancellation
            window, you will be charged{' '}
            {formatPolicyWindowRemoveWorkerCharge(policyWindow, shift)}.
          </Message>
          <Box mb={3}>
            <Text fontWeight="bold" mb={2}>
              Your cancellation policy is as follows:
            </Text>
            <ul
              style={{
                listStyle: 'disc',
                marginLeft: '1em',
                marginBottom: '1em',
              }}
            >
              {cancellationPolicyWindows.map((policy) => {
                return (
                  <li key={getPolicyWindowKey(policy)}>
                    Cancellations made within {policy.cutoffTimeHours} hours of
                    the shift start time will be charged{' '}
                    {formatPolicyWindowListCharge(policy)}.
                  </li>
                )
              })}
            </ul>
          </Box>
          {isStandard ? (
            <Box mt={3}>
              <BaseLink
                onClick={() => {
                  window.open(policyUrl, '_blank')
                }}
                href={policyUrl}
                target={'_blank'}
                rel="noreferrer"
              >
                See policy
              </BaseLink>
            </Box>
          ) : null}
        </>
      ) : null}
    </Modal>
  )
}
